import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, RotateCcw, Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";

export default function DepartmentsAdminPage() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/departments", { params: { include_deleted: includeDeleted } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeDeleted]);

  const submit = async () => {
    const departmentName = name.trim();

    if (!departmentName) {
      return toast.error("Please enter a name");
    }

    try {
      await api.post("/departments", { name: departmentName });
      toast.success("Department created successfully");
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const saveEdit = async () => {
    const departmentName = (editing.name || "").trim();

    if (!departmentName) {
      return toast.error("Please enter a name");
    }

    try {
      await api.patch(`/departments/${editing.id}`, { name: departmentName, status: editing.status });
      toast.success("Department updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Department deleted successfully");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmDelete(null);
    }
  };
  const doRestore = async (id) => {
    try {
      await api.post(`/departments/${id}/restore`);
      toast.success("Department restored successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-departments-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Departments</h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Show deleted</span>
          <Switch data-testid="dept-show-deleted" checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <div className="rounded-xl border border-border bg-card/70 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} data-row data-testid={`dept-row-${d.id}`} className={`border-b border-border/70 ${d.deleted ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3">{d.name}</td>
                  <td className="px-4 py-3 text-xs">{d.deleted ? "deleted" : d.status}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {d.deleted ? (
                      <Button variant="outline" size="sm" onClick={() => doRestore(d.id)} data-testid={`dept-restore-${d.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditing({ ...d })} data-testid={`dept-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>{" "}
                        <Button variant="outline" size="sm" onClick={() => setConfirmDelete(d)} data-testid={`dept-delete-${d.id}`} className="text-rose-300 border-rose-500/30 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground text-xs">No departments yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-border bg-card/70 p-4 space-y-3 h-fit">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Add department</div>
          <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="dept-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <Button data-testid="dept-create" onClick={submit} className="w-full bg-[#d4af37] text-black hover:bg-[#e5c158]">Add</Button>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-dept-dialog">
          <DialogHeader><DialogTitle>Edit department</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="edit-dept-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="edit-dept-save" onClick={saveEdit} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : "Delete?"}
        description="Soft delete — the department will be hidden. Departments with open tickets cannot be deleted."
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDelete && doDelete(confirmDelete.id)}
        testid="confirm-delete-dept"
      />
    </div>
  );
}
